import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getGroupTypes, getGroupRolesByType } from '../../endpoints/groups';
import { GroupType, GroupRole, GroupPermission } from '../../types/group';

const SCOPES = [
  { value: 'international', label: 'International' },
  { value: 'spd_chapter', label: 'SPD Chapter' },
  { value: 'local_chapter', label: 'Local Chapter' },
] as const;

interface LocationState {
  initialSelections?: GroupPermission[];
  enableRoleSelection?: boolean;
  onSave?: (selections: GroupPermission[]) => void;
  returnPath?: string;
}

const GroupPermissions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  // Memoize initialSelections to prevent unnecessary re-renders
  const initialSelections = useMemo(
    () => state?.initialSelections || [],
    [state?.initialSelections]
  );
  const enableRoleSelection = state?.enableRoleSelection ?? true;
  const returnPath = state?.returnPath || '/library';

  // Track if we've initialized to prevent re-initialization
  const hasInitializedRef = useRef(false);

  // State for selected groups: Map<group_type_id, GroupPermission>
  const [selectedGroups, setSelectedGroups] = useState<
    Map<string, GroupPermission>
  >(new Map());

  // State for expanded groups (showing role selection)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // State for group type search inputs per scope
  const [groupTypeSearches, setGroupTypeSearches] = useState<
    Record<string, string>
  >({
    international: '',
    spd_chapter: '',
    local_chapter: '',
  });

  // Fetch group types for all scopes
  const {
    data: internationalGroups = [],
    isLoading: loadingInternational,
    error: internationalError,
  } = useApiGet<GroupType[]>(
    ['group-types', 'international'],
    () => getGroupTypes('international'),
    {
      enabled: true,
    }
  );

  const {
    data: spdGroups = [],
    isLoading: loadingSpd,
    error: spdError,
  } = useApiGet<GroupType[]>(
    ['group-types', 'spd_chapter'],
    () => getGroupTypes('spd_chapter'),
    {
      enabled: true,
    }
  );

  const {
    data: localGroups = [],
    isLoading: loadingLocal,
    error: localError,
  } = useApiGet<GroupType[]>(
    ['group-types', 'local_chapter'],
    () => getGroupTypes('local_chapter'),
    {
      enabled: true,
    }
  );

  // Initialize selected groups from initialSelections
  useEffect(() => {
    // Only initialize once, and only if we have groups loaded and initial selections
    if (
      hasInitializedRef.current ||
      initialSelections.length === 0 ||
      (internationalGroups.length === 0 &&
        spdGroups.length === 0 &&
        localGroups.length === 0)
    ) {
      return;
    }

    const initialMap = new Map<string, GroupPermission>();
    const initialExpanded = new Set<string>();
    const initialScopeMap = new Map<string, string>();

    // Combine all groups to search for scope
    const allGroups = [
      ...internationalGroups.map((gt) => ({ ...gt, scope: 'international' })),
      ...spdGroups.map((gt) => ({ ...gt, scope: 'spd_chapter' })),
      ...localGroups.map((gt) => ({ ...gt, scope: 'local_chapter' })),
    ];

    initialSelections.forEach((perm) => {
      initialMap.set(perm.group_type_id, perm);
      // If role selection is enabled and not all_roles, expand to show roles
      if (enableRoleSelection && !perm.all_roles && perm.role_ids) {
        initialExpanded.add(perm.group_type_id);
      }

      // Find which scope this group belongs to
      const groupWithScope = allGroups.find(
        (gt) => gt.id === perm.group_type_id
      );
      if (groupWithScope) {
        initialScopeMap.set(perm.group_type_id, groupWithScope.scope);
      }
    });

    setSelectedGroups(initialMap);
    setExpandedGroups(initialExpanded);
    setGroupScopeMap(initialScopeMap);
    hasInitializedRef.current = true;
  }, [
    initialSelections,
    internationalGroups,
    spdGroups,
    localGroups,
    enableRoleSelection,
  ]); // Run when groups are loaded

  // Track which scope each group belongs to
  const [groupScopeMap, setGroupScopeMap] = useState<Map<string, string>>(
    new Map()
  );

  // Filter group types based on search for each scope
  const filteredGroupsByScope = useMemo(() => {
    const selectedIds = new Set(selectedGroups.keys());
    return {
      international: internationalGroups.filter(
        (gt) =>
          !selectedIds.has(gt.id) &&
          gt.name
            .toLowerCase()
            .includes(groupTypeSearches.international.toLowerCase())
      ),
      spd_chapter: spdGroups.filter(
        (gt) =>
          !selectedIds.has(gt.id) &&
          gt.name
            .toLowerCase()
            .includes(groupTypeSearches.spd_chapter.toLowerCase())
      ),
      local_chapter: localGroups.filter(
        (gt) =>
          !selectedIds.has(gt.id) &&
          gt.name
            .toLowerCase()
            .includes(groupTypeSearches.local_chapter.toLowerCase())
      ),
    };
  }, [
    internationalGroups,
    spdGroups,
    localGroups,
    selectedGroups,
    groupTypeSearches,
  ]);

  // Handle group type selection
  const handleGroupTypeSelect = (
    groupType: GroupType | null,
    scope: string
  ) => {
    if (!groupType) return;

    const newPermission: GroupPermission = {
      group_type_id: groupType.id,
      all_roles: !enableRoleSelection || false,
      role_ids: !enableRoleSelection ? undefined : [],
    };

    setSelectedGroups((prev) => {
      const updated = new Map(prev);
      updated.set(groupType.id, newPermission);
      return updated;
    });

    // Track which scope this group belongs to
    setGroupScopeMap((prev) => {
      const updated = new Map(prev);
      updated.set(groupType.id, scope);
      return updated;
    });

    // Clear search input for this scope
    setGroupTypeSearches((prev) => ({
      ...prev,
      [scope]: '',
    }));

    // If role selection is enabled, expand to show role selection
    if (enableRoleSelection) {
      setExpandedGroups((prev) => new Set(prev).add(groupType.id));
    }
  };

  // Remove a group from selection
  const handleRemoveGroup = (groupTypeId: string) => {
    setSelectedGroups((prev) => {
      const updated = new Map(prev);
      updated.delete(groupTypeId);
      return updated;
    });
    setExpandedGroups((prev) => {
      const updated = new Set(prev);
      updated.delete(groupTypeId);
      return updated;
    });
  };

  // Toggle expand/collapse for role selection
  const handleToggleExpand = (groupTypeId: string) => {
    setExpandedGroups((prev) => {
      const updated = new Set(prev);
      if (updated.has(groupTypeId)) {
        updated.delete(groupTypeId);
      } else {
        updated.add(groupTypeId);
      }
      return updated;
    });
  };

  // Handle "Select All Roles" checkbox
  const handleSelectAllRoles = (groupTypeId: string, checked: boolean) => {
    setSelectedGroups((prev) => {
      const updated = new Map(prev);
      const current = updated.get(groupTypeId);
      if (current) {
        updated.set(groupTypeId, {
          ...current,
          all_roles: checked,
          role_ids: checked ? undefined : [],
        });
      }
      return updated;
    });
  };

  // Handle individual role selection
  const handleRoleToggle = (groupTypeId: string, roleId: string) => {
    setSelectedGroups((prev) => {
      const updated = new Map(prev);
      const current = updated.get(groupTypeId);
      if (current) {
        const currentRoleIds = current.role_ids || [];
        const newRoleIds = currentRoleIds.includes(roleId)
          ? currentRoleIds.filter((id) => id !== roleId)
          : [...currentRoleIds, roleId];

        updated.set(groupTypeId, {
          ...current,
          all_roles: false,
          role_ids: newRoleIds,
        });
      }
      return updated;
    });
  };

  // Handle save
  const handleSave = () => {
    const selections = Array.from(selectedGroups.values());
    if (state?.onSave) {
      state.onSave(selections);
    }
    navigate(returnPath, { state: { groupPermissions: selections } });
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(returnPath);
  };

  // Get group type name by ID
  const getGroupTypeName = (groupTypeId: string): string => {
    const scope = groupScopeMap.get(groupTypeId);
    if (!scope) {
      // Try to find in any of the groups
      const allGroups = [...internationalGroups, ...spdGroups, ...localGroups];
      const groupType = allGroups.find((gt) => gt.id === groupTypeId);
      return groupType?.name || groupTypeId;
    }

    const scopeGroups =
      scope === 'international'
        ? internationalGroups
        : scope === 'spd_chapter'
          ? spdGroups
          : localGroups;
    const groupType = scopeGroups.find((gt) => gt.id === groupTypeId);
    return groupType?.name || groupTypeId;
  };

  // Get scope label for a selected group
  const getGroupScopeLabel = (groupTypeId: string): string => {
    const scope = groupScopeMap.get(groupTypeId);
    if (!scope) return '';
    const scopeObj = SCOPES.find((s) => s.value === scope);
    return scopeObj?.label || scope;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Manage Group Permissions
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Side - Available Groups by Scope */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Available Groups
            </Typography>

            {/* Render each scope section */}
            {SCOPES.map((scope) => {
              const groups = filteredGroupsByScope[scope.value];
              const isLoading =
                scope.value === 'international'
                  ? loadingInternational
                  : scope.value === 'spd_chapter'
                    ? loadingSpd
                    : loadingLocal;
              const error =
                scope.value === 'international'
                  ? internationalError
                  : scope.value === 'spd_chapter'
                    ? spdError
                    : localError;

              return (
                <Box key={scope.value} sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: 'bold' }}
                  >
                    {scope.label}
                  </Typography>

                  {/* Search for this scope */}
                  <Autocomplete
                    options={groups}
                    getOptionLabel={(option) => option.name}
                    value={null}
                    inputValue={groupTypeSearches[scope.value]}
                    onInputChange={(_, newValue) =>
                      setGroupTypeSearches((prev) => ({
                        ...prev,
                        [scope.value]: newValue,
                      }))
                    }
                    onChange={(_, newValue) =>
                      handleGroupTypeSelect(newValue, scope.value)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={`Search ${scope.label.toLowerCase()} groups...`}
                        size="small"
                        fullWidth
                      />
                    )}
                    loading={isLoading}
                    disabled={isLoading}
                    sx={{ mb: 1 }}
                  />

                  {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      Failed to load {scope.label.toLowerCase()} groups.
                    </Alert>
                  )}

                  {/* Groups List for this scope */}
                  {isLoading ? (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', py: 2 }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  ) : groups.length > 0 ? (
                    <List dense>
                      {groups.map((groupType) => (
                        <ListItem
                          key={groupType.id}
                          disablePadding
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={() =>
                                handleGroupTypeSelect(groupType, scope.value)
                              }
                              size="small"
                              sx={{ color: '#0072CE' }}
                            >
                              <AddIcon />
                            </IconButton>
                          }
                        >
                          <ListItemButton
                            onClick={() =>
                              handleGroupTypeSelect(groupType, scope.value)
                            }
                          >
                            <ListItemText primary={groupType.name} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 1, pl: 2 }}
                    >
                      {groupTypeSearches[scope.value]
                        ? 'No groups found matching your search'
                        : 'No groups available'}
                    </Typography>
                  )}

                  {scope.value !== SCOPES[SCOPES.length - 1].value && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              );
            })}
          </Paper>
        </Grid>

        {/* Right Side - Selected Groups */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">
                Selected Groups ({selectedGroups.size})
              </Typography>
            </Box>

            {selectedGroups.size > 0 ? (
              <List>
                {Array.from(selectedGroups.entries()).map(
                  ([groupTypeId, permission]) => (
                    <GroupPermissionItem
                      key={groupTypeId}
                      groupTypeId={groupTypeId}
                      groupTypeName={getGroupTypeName(groupTypeId)}
                      permission={permission}
                      expanded={expandedGroups.has(groupTypeId)}
                      enableRoleSelection={enableRoleSelection}
                      onToggleExpand={handleToggleExpand}
                      onSelectAllRoles={handleSelectAllRoles}
                      onRoleToggle={handleRoleToggle}
                      onRemove={handleRemoveGroup}
                      getGroupScopeLabel={getGroupScopeLabel}
                    />
                  )
                )}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No groups selected. Select groups from the left panel.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Footer Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={handleCancel} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={selectedGroups.size === 0}
          sx={{
            backgroundColor: '#0072CE',
            '&:hover': { backgroundColor: '#005a9e' },
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

// Sub-component for individual group permission item
interface GroupPermissionItemProps {
  groupTypeId: string;
  groupTypeName: string;
  permission: GroupPermission;
  expanded: boolean;
  enableRoleSelection: boolean;
  onToggleExpand: (groupTypeId: string) => void;
  onSelectAllRoles: (groupTypeId: string, checked: boolean) => void;
  onRoleToggle: (groupTypeId: string, roleId: string) => void;
  onRemove: (groupTypeId: string) => void;
}

const GroupPermissionItem: React.FC<
  GroupPermissionItemProps & { getGroupScopeLabel: (id: string) => string }
> = ({
  groupTypeId,
  groupTypeName,
  permission,
  expanded,
  enableRoleSelection,
  onToggleExpand,
  onSelectAllRoles,
  onRoleToggle,
  onRemove,
  getGroupScopeLabel,
}) => {
  // Fetch roles for this group type
  const {
    data: roles = [],
    isLoading: loadingRoles,
    error: rolesError,
  } = useApiGet<GroupRole[]>(
    ['group-roles', groupTypeId],
    () => getGroupRolesByType(groupTypeId),
    {
      enabled: expanded && enableRoleSelection,
    }
  );

  // Sort roles by order if available, then by name
  const sortedRoles = useMemo(() => {
    return [...roles].sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [roles]);

  return (
    <ListItem
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        flexDirection: 'column',
        alignItems: 'stretch',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            flexWrap: 'wrap',
          }}
        >
          <Chip
            label={groupTypeName}
            color="primary"
            variant="outlined"
            size="small"
          />
          <Chip
            label={getGroupScopeLabel(groupTypeId)}
            color="secondary"
            variant="outlined"
            size="small"
          />
          {permission.all_roles ? (
            <Chip label="All Roles" color="success" size="small" />
          ) : (
            permission.role_ids &&
            permission.role_ids.length > 0 && (
              <Chip
                label={`${permission.role_ids.length} role${
                  permission.role_ids.length > 1 ? 's' : ''
                }`}
                size="small"
              />
            )
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {enableRoleSelection && (
            <IconButton
              size="small"
              onClick={() => onToggleExpand(groupTypeId)}
              sx={{ color: '#0072CE' }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => onRemove(groupTypeId)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Role Selection (when expanded) */}
      {expanded && enableRoleSelection && (
        <Box sx={{ mt: 2, width: '100%', pl: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={permission.all_roles}
                onChange={(e) =>
                  onSelectAllRoles(groupTypeId, e.target.checked)
                }
                size="small"
              />
            }
            label="Select All Roles"
            sx={{ mb: 1 }}
          />

          {loadingRoles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : rolesError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              Failed to load roles. Please try again.
            </Alert>
          ) : (
            <Box>
              {sortedRoles.map((role) => (
                <FormControlLabel
                  key={role.id}
                  control={
                    <Checkbox
                      checked={
                        permission.all_roles ||
                        (permission.role_ids || []).includes(role.id)
                      }
                      onChange={() => onRoleToggle(groupTypeId, role.id)}
                      disabled={permission.all_roles}
                      size="small"
                    />
                  }
                  label={role.name}
                  sx={{ display: 'block', mb: 0.5 }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </ListItem>
  );
};

export default GroupPermissions;
